import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { toast } from "sonner";
import { ArrowRight, Link as LinkIcon, LayoutDashboard } from "lucide-react";
import { clearToken } from "@/lib/auth";
import { ModuleCatalog } from "@/components/ModuleCatalog";

const copyLink = async (url) => {
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Lien copié", { description: url });
  } catch {
    toast.error("Impossible de copier le lien");
  }
};

const CardActions = ({ module }) => {
  const shareUrl = `${window.location.origin}/p/${module.key}`;
  return (
    <div className="pt-4 border-t border-[var(--border)] flex flex-wrap items-center justify-between gap-3">
      <Link
        to={module.path}
        data-testid={`module-start-${module.key}`}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--gold)] text-white text-sm font-medium hover:bg-[var(--gold-light)] transition-all shadow-sm active:scale-95"
      >
        Ouvrir <ArrowRight size={16} />
      </Link>
      {module.key === "super-cerveau" ? (
        <Link to="/praticien/tableau-de-bord#activations" data-testid="module-activation-super-cerveau" className="inline-flex items-center gap-1.5 text-xs text-[var(--ink-soft)] hover:text-[var(--gold)] transition-colors py-1 px-2 rounded-lg">
          <LinkIcon size={14} /> Lien d'activation (tableau de bord)
        </Link>
      ) : (
      <button
        onClick={() => copyLink(shareUrl)}
        data-testid={`module-copy-${module.key}`}
        className="inline-flex items-center gap-1.5 text-xs text-[var(--ink-soft)] hover:text-[var(--gold)] transition-colors py-1 px-2 rounded-lg hover:bg-[var(--sand)]/60"
      >
        <LinkIcon size={14} /> Copier le lien patient
      </button>
      )}
      <span className="sr-only" data-testid={`module-link-${module.key}`}>{shareUrl}</span>
    </div>
  );
};

export default function Landing() {
  const navigate = useNavigate();
  return (
    <Layout wide>
      <div className="animate-fade-up">
        <div className="flex justify-end mb-6">
          <Link
            to="/praticien/tableau-de-bord"
            data-testid="landing-praticien-link"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[var(--gold)] text-[var(--gold)] text-sm font-medium hover:bg-[var(--gold)] hover:text-white transition-all"
          >
            <LayoutDashboard size={16} /> Espace praticien
          </Link>
        </div>
        <div className="text-center mb-12">
          <h1 className="font-serif-title text-4xl sm:text-5xl text-[var(--accent-deep)] mb-3 tracking-tight">Nos outils cliniques</h1>
          <p className="text-[var(--ink-soft)] max-w-xl mx-auto leading-relaxed">
            Ouvrez un module pour le faire passer vous-même, ou copiez le <strong>lien patient</strong>
            {" "}(sans accès à votre espace) à envoyer directement à la personne.
          </p>
        </div>

        <ModuleCatalog cardTestid="module-card" renderActions={(m) => <CardActions module={m} />} />

        <div className="text-center mt-14">
          <button
            onClick={() => { clearToken(); navigate("/praticien"); }}
            data-testid="landing-logout-link"
            className="text-sm text-[var(--ink-soft)] hover:text-[var(--gold)] transition-colors border-b border-transparent hover:border-[var(--gold)] pb-0.5"
          >
            Se déconnecter
          </button>
        </div>
      </div>
    </Layout>
  );
}
